import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { deleteAccount, exportAccount, getMe, logout } from '../lib/api.js';

/** Konto-Übersicht + DSGVO-Selbstbedienung (Datenexport, Kontolöschung). */
export function Account(): JSX.Element {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['auth-me'], queryFn: getMe });
  const user = data?.user;

  const exportMut = useMutation({
    mutationFn: exportAccount,
    onSuccess: (json) => {
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `betongold-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      qc.clear();
      window.location.href = '/';
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  const handleLogout = async (): Promise<void> => {
    await logout();
    qc.clear();
    window.location.href = '/';
  };

  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Title order={1}>Mein Konto</Title>
        <Text c="dimmed" size="sm">
          Kontodaten, Datenexport und Löschung.
        </Text>
      </Stack>

      <Card padding="lg">
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap">
            <Stack gap={2}>
              <Text fw={700} size="lg">
                {user?.name ?? user?.email ?? '—'}
              </Text>
              <Text size="sm" c="dimmed">
                {user?.email}
              </Text>
            </Stack>
            <Group gap={6}>
              {user?.isOwner && (
                <Badge color="violet" variant="light" tt="none">
                  Betreiber
                </Badge>
              )}
              {user && !user.viaSession && (
                <Badge color="orange" variant="light" tt="none">
                  Übergangsmodus (kein Login)
                </Badge>
              )}
            </Group>
          </Group>
          <Group gap="xs" mt="sm">
            <Anchor component={Link} to="/settings" size="sm">
              ⚙️ Zu den Einstellungen
            </Anchor>
            {user?.isOwner && (
              <Anchor component={Link} to="/admin/users" size="sm">
                · 👥 Nutzerverwaltung
              </Anchor>
            )}
          </Group>
        </Stack>
      </Card>

      <Card padding="lg">
        <Stack gap="md">
          <Title order={4}>Deine Daten</Title>
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text size="sm" c="dimmed" style={{ flex: 1, minWidth: 200 }}>
              Lade alle über dich gespeicherten Daten als JSON herunter (Objekte, Anker,
              Einstellungen, Verlauf).
            </Text>
            <Button variant="light" onClick={() => exportMut.mutate()} loading={exportMut.isPending}>
              📥 Daten exportieren
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card padding="lg">
        <Stack gap="md">
          {user?.viaSession && (
            <Button variant="default" onClick={handleLogout}>
              Abmelden
            </Button>
          )}
          {!user?.isOwner && user?.viaSession && !data?.impersonating && (
            <Group justify="space-between" wrap="wrap" gap="sm">
              <Text size="sm" c="dimmed" style={{ flex: 1, minWidth: 200 }}>
                Konto und alle zugehörigen Daten unwiderruflich löschen.
              </Text>
              <Button
                color="red"
                variant="light"
                onClick={() => {
                  if (confirm('Konto wirklich unwiderruflich löschen? Alle deine Daten gehen verloren.'))
                    deleteMut.mutate();
                }}
                loading={deleteMut.isPending}
              >
                Konto löschen
              </Button>
            </Group>
          )}
          {data?.impersonating && (
            <Text size="xs" c="dimmed">
              Kontolöschung ist im „Ansehen als"-Modus deaktiviert.
            </Text>
          )}
          {user?.isOwner && (
            <Text size="xs" c="dimmed">
              Der Betreiber-Account kann nicht per Selbstbedienung gelöscht werden.
            </Text>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
